import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { ForkKnife, Flame, Lightning, Note, ShoppingCart, CheckSquare, Square, Robot } from "@phosphor-icons/react";
import AskCoachAI from "@/components/AskCoachAI";

const CATEGORIES = ["Produce", "Protein", "Pantry", "Dairy", "Frozen", "Other"];

export default function ClientMealsPage() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [activeDay, setActiveDay] = useState(0);
  const [view, setView] = useState("meals"); // "meals" | "shopping"
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/meal-plan");
        setPlan(data?.plan || null);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Tick / untick a shopping-list item. Optimistic update; rolls back on failure.
  const toggleShoppingItem = async (index, nextChecked) => {
    setPlan((cur) => {
      const items = [...(cur.shopping_list?.items || [])];
      if (items[index]) items[index] = { ...items[index], checked: nextChecked };
      return { ...cur, shopping_list: { ...cur.shopping_list, items } };
    });
    try {
      await http.patch("/client/shopping-list/toggle", { index, checked: nextChecked });
    } catch (e) {
      // rollback
      setPlan((cur) => {
        const items = [...(cur.shopping_list?.items || [])];
        if (items[index]) items[index] = { ...items[index], checked: !nextChecked };
        return { ...cur, shopping_list: { ...cur.shopping_list, items } };
      });
      setErr(apiError(e, "Could not update — try again."));
    }
  };

  if (loading) {
    return <div className="p-6 md:p-10 max-w-5xl mx-auto"><div className="id-label text-zinc-500 id-pulse">— Loading meal plan —</div></div>;
  }

  if (err) {
    return <div className="p-6 md:p-10 max-w-5xl mx-auto"><div className="id-card border-[#FF3333] text-[#FF3333] text-sm">{err}</div></div>;
  }

  if (!plan) {
    return (
      <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="client-meals-empty">
        <div className="id-card text-center py-12">
          <ForkKnife size={32} weight="bold" className="text-zinc-600 mx-auto mb-4" />
          <h2 className="id-h2 mb-2">No meal plan yet.</h2>
          <p className="text-zinc-400 font-manrope">Your coach is building it. You&apos;ll see it here as soon as it&apos;s ready.</p>
        </div>
      </div>
    );
  }

  const day = plan.days?.[activeDay];
  const filledMeals = (day?.meals || []).filter((m) => (m.title || "").trim().length > 0);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="client-meals-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#FF3333] flex items-center justify-center">
          <ForkKnife size={20} weight="bold" color="#fff" />
        </div>
        <div>
          <div className="id-label">Your meal plan</div>
          <h1 className="font-oswald text-3xl font-bold uppercase tracking-wide leading-tight">{plan.name}</h1>
        </div>
      </div>

      {/* Targets */}
      {(plan.daily_calorie_target || plan.daily_protein_target) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="client-meals-targets">
          {plan.daily_calorie_target && (
            <div className="border border-[#FF3333] bg-[#FF3333]/5 px-4 py-3">
              <div className="text-[10px] text-[#FF3333] uppercase tracking-[0.15em] font-bold flex items-center gap-1"><Flame size={12} weight="bold" /> Daily calories</div>
              <div className="font-oswald text-3xl font-bold text-white">{plan.daily_calorie_target}<span className="text-zinc-500 text-sm font-manrope font-normal ml-1">kcal</span></div>
            </div>
          )}
          {plan.daily_protein_target && (
            <div className="border border-[#262626] bg-[#0F0F0F] px-4 py-3">
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold flex items-center gap-1"><Lightning size={12} weight="bold" /> Daily protein</div>
              <div className="font-oswald text-3xl font-bold text-white">{plan.daily_protein_target}<span className="text-zinc-500 text-sm font-manrope font-normal ml-1">g</span></div>
            </div>
          )}
        </div>
      )}

      {/* Coach notes */}
      {plan.coach_notes && (
        <div className="border-l-2 border-[#FF3333] bg-[#FF3333]/5 px-4 py-3 mb-6" data-testid="client-meals-notes">
          <div className="text-[10px] text-[#FF3333] uppercase tracking-[0.15em] font-bold flex items-center gap-1 mb-1"><Note size={12} weight="bold" /> Coach notes</div>
          <p className="text-zinc-200 text-sm font-manrope whitespace-pre-wrap leading-relaxed">{plan.coach_notes}</p>
        </div>
      )}

      {/* View toggle: Meals vs Shopping List (only shows if SL exists) */}
      {plan.shopping_list?.items?.length > 0 && (
        <div className="flex gap-1 border-b border-[#262626] mb-4" data-testid="client-view-toggle">
          <button
            onClick={() => setView("meals")}
            data-testid="client-view-meals"
            className={`px-5 py-3 uppercase tracking-[0.15em] text-xs font-bold border-b-2 transition-colors ${view === "meals" ? "border-[#FF3333] text-white" : "border-transparent text-zinc-500"}`}
          >
            <ForkKnife size={13} weight="bold" className="inline mr-2" /> Meals
          </button>
          <button
            onClick={() => setView("shopping")}
            data-testid="client-view-shopping"
            className={`px-5 py-3 uppercase tracking-[0.15em] text-xs font-bold border-b-2 transition-colors ${view === "shopping" ? "border-[#FF3333] text-white" : "border-transparent text-zinc-500"}`}
          >
            <ShoppingCart size={13} weight="bold" className="inline mr-2" /> Shopping List
            <span className="ml-2 text-[10px] bg-[#FF3333]/20 text-[#FF3333] px-1.5 py-0.5">
              {plan.shopping_list.items.filter((i) => !i.checked).length}
            </span>
          </button>
        </div>
      )}

      {view === "meals" && (
        <>
      {/* Day tabs */}
      <div className="flex gap-1 overflow-x-auto mb-4" data-testid="client-meals-day-tabs">
        {(plan.days || []).map((d, i) => (
          <button key={i} onClick={() => setActiveDay(i)} data-testid={`client-meal-day-${i}`}
            className={`flex-shrink-0 px-4 py-2 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${activeDay === i ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
            {d.name}
          </button>
        ))}
      </div>

      {/* Meals */}
      {day && (
        <div className="space-y-3" data-testid="client-meals-day">
          {filledMeals.map((m, mi) => (
            <div key={mi} className="id-card" data-testid={`client-meal-${mi}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="id-label text-[#FF3333]">{m.slot}</div>
                <div className="flex gap-3 text-xs text-zinc-500 font-manrope">
                  {m.calories ? <span>{m.calories} kcal</span> : null}
                  {m.protein_g ? <span>{m.protein_g}g P</span> : null}
                </div>
              </div>
              <div className="text-white font-manrope text-base leading-snug mb-2">{m.title}</div>
              {m.notes && (
                <p className="text-zinc-400 text-sm font-manrope whitespace-pre-wrap leading-relaxed">{m.notes}</p>
              )}
            </div>
          ))}
          {filledMeals.length === 0 && (
            <div className="text-center text-zinc-500 text-sm font-manrope py-12 border border-[#262626]">
              No meals set for this day yet — your coach is still building it.
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Shopping List view */}
      {view === "shopping" && plan.shopping_list?.items?.length > 0 && (
        <div className="space-y-5" data-testid="client-shopping-list">
          <p className="text-zinc-400 text-sm font-manrope">
            Tick items off as you shop — progress saves automatically.{" "}
            <span className="text-zinc-600">
              {plan.shopping_list.items.filter((i) => i.checked).length} / {plan.shopping_list.items.length} done
            </span>
          </p>
          {CATEGORIES.map((cat) => {
            const catItems = plan.shopping_list.items
              .map((it, i) => ({ ...it, __i: i }))
              .filter((it) => (it.category || "Other") === cat);
            if (catItems.length === 0) return null;
            const remaining = catItems.filter((i) => !i.checked).length;
            return (
              <div key={cat} data-testid={`client-sl-cat-${cat.toLowerCase()}`}>
                <div className="id-label text-[#FF3333] mb-2 flex items-center justify-between">
                  <span>— {cat} —</span>
                  <span className="text-zinc-600">{remaining} left</span>
                </div>
                <div className="space-y-1">
                  {catItems.map((it) => (
                    <button
                      key={it.__i}
                      onClick={() => toggleShoppingItem(it.__i, !it.checked)}
                      data-testid={`client-sl-item-${it.__i}`}
                      className={`w-full flex items-center gap-3 border px-3 py-2.5 transition-colors text-left ${it.checked ? "border-[#262626] bg-[#0F0F0F] opacity-50" : "border-[#262626] bg-[#0F0F0F] hover:border-[#FF3333]/40"}`}
                    >
                      {it.checked ? (
                        <CheckSquare size={20} weight="fill" className="text-[#FF3333] shrink-0" />
                      ) : (
                        <Square size={20} weight="bold" className="text-zinc-600 shrink-0" />
                      )}
                      <span className={`flex-1 font-manrope ${it.checked ? "line-through text-zinc-500" : "text-white"}`}>{it.name}</span>
                      {it.quantity && (
                        <span className={`text-xs font-manrope ${it.checked ? "text-zinc-600" : "text-zinc-400"}`}>{it.quantity}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating "Ask Coach AI" button — always visible on meals page.
          Positioned above the Emergent badge (which lives at bottom-right). */}
      <button
        onClick={() => setAskOpen(true)}
        data-testid="ask-coach-ai-open"
        className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-40 bg-[#FF3333] hover:bg-[#E62929] text-white border border-[#FF3333] shadow-[0_8px_24px_rgba(255,51,51,0.4)] flex items-center gap-2 px-4 py-3 uppercase tracking-[0.1em] text-xs font-bold transition-transform hover:scale-105"
      >
        <Robot size={18} weight="fill" />
        Ask Coach AI
      </button>

      <AskCoachAI open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}
